import React, { CSSProperties, HTMLAttributes } from 'react';
import styled from 'styled-components';
import theme from '@/styles/theme';

export enum ButtonColorType {
  PRIMARY = 'PRIMARY',
  BLUE = 'BLUE',
  RED = 'RED',
  GREEN = 'GREEN',
  YELLOW = 'YELLOW',
  WHITE = 'WHITE',
  DEFAULT = 'DEFAULT'
}

interface LineButtonProps {
  type: ButtonColorType;
  onClick: HTMLAttributes<HTMLElement>['onClick'];
  children: string | Element;
  size?: 'inline' | 'default';
  disabled?: boolean;
  style?: CSSProperties;
}

const getLineColor = (colorType: ButtonColorType): string => {
  if (colorType === ButtonColorType.WHITE) {
    return theme.DF_COLOR[colorType];
  }
  if (colorType === ButtonColorType.DEFAULT) {
    return theme.DF_COLOR.GRAY100;
  }
  return theme.DF_COLOR[`${colorType}500`];
};

const getHoverColor = (colorType: ButtonColorType): string => {
  if (colorType === ButtonColorType.WHITE) {
    return theme.DF_COLOR.GRAY100;
  }
  if (colorType === ButtonColorType.DEFAULT) {
    return theme.DF_COLOR.GRAY50;
  }
  return theme.DF_COLOR[`${colorType}200`];
};

const getTextColor = (colorType: ButtonColorType): string => {
  if (
    colorType === ButtonColorType.WHITE ||
    colorType === ButtonColorType.DEFAULT
  ) {
    return theme.DF_COLOR.GRAY400;
  }
  return theme.DF_COLOR[`${colorType}700`];
};

const LineButton = (props: LineButtonProps) => {
  const { type, onClick, disabled, ...rest } = props;
  const lineColor = getLineColor(type);
  const hoverColor = getHoverColor(type);
  const textColor = getTextColor(type);

  return (
    <LineButtonWrapper
      disabled={disabled}
      colorType={type}
      lineColor={lineColor}
      hoverColor={hoverColor}
      textColor={textColor}
      size={props.size}
      onClick={onClick}
      {...rest}>
      {props.children}
    </LineButtonWrapper>
  );
};

export default LineButton;

const LineButtonWrapper = styled.button<{
  colorType: ButtonColorType;
  lineColor: string;
  hoverColor: string;
  textColor: string;
  size?: 'inline' | 'default';
}>`
  ${({ size }) =>
    size === 'inline' ? theme.DF_TYPOGRAPHY.UI14M : theme.DF_TYPOGRAPHY.UI16M};
  border: ${({ lineColor }) => `1px solid ${lineColor}`};
  color: ${({ textColor }) => textColor};
  box-shadow: none;
  background: ${theme.DF_COLOR.WHITE};
  padding: ${({ size }) => (size === 'inline' ? '4px 8px' : '8px 16px')};
  border-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background 0.4s;

  &:disabled {
    border: ${`1px solid ${theme.DF_COLOR.GRAY200}`};
    background: ${theme.DF_COLOR.GRAY50};
    color: ${theme.DF_COLOR.GRAY300};
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      background: ${theme.DF_COLOR.GRAY50};
    }
  }

  &:hover {
    background: ${({ hoverColor }) => hoverColor};
  }
`;
